import { useMemo, useRef, useState } from 'react';
import { useProjectContext } from '../contexts/ProjectContext';
import {
  useRilievi,
  useCreaRilievo,
  useAggiornaRilievo,
  useEliminaRilievo,
  useCaricaFotoRilievo,
} from '../hooks';
import { FotoProtetta } from '../components/Rilievi/FotoProtetta';
import { STANZE_SUGGERITE } from '../components/Rilievi/stanze';
import { preparaFoto, formatoPeso, type FilePronto } from '../utils/immagini';
import type { Lavoro, Rilievo as RilievoType } from '../types';

const oggi = () => new Date().toISOString().slice(0, 10);

export function Rilievi() {
  const { currentProject } = useProjectContext();
  const projectId = currentProject?.id;

  const { data: rilievi = [], isLoading } = useRilievi(projectId);
  const creaRilievo = useCreaRilievo();
  const aggiornaRilievo = useAggiornaRilievo();
  const eliminaRilievo = useEliminaRilievo();
  const caricaFoto = useCaricaFotoRilievo();

  const lavori: Lavoro[] = currentProject?.lavori || [];

  const [filtroStanza, setFiltroStanza] = useState('');
  const [filtroLavoro, setFiltroLavoro] = useState('');
  const [nuovo, setNuovo] = useState<{ stanza: string; lavoroId: string; titolo: string; testo: string } | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [ingrandita, setIngrandita] = useState<{ id: string; nome: string } | null>(null);

  // Le stanze già usate vengono prima: dopo due giorni di cantiere sono quelle che servono
  const stanzeUsate = useMemo(
    () => [...new Set(rilievi.map((r) => r.stanza))].sort((a, b) => a.localeCompare(b)),
    [rilievi]
  );
  const stanzeSuggerite = useMemo(
    () => [...stanzeUsate, ...STANZE_SUGGERITE.filter((s) => !stanzeUsate.includes(s))],
    [stanzeUsate]
  );

  const visibili = useMemo(
    () =>
      rilievi.filter(
        (r) =>
          (!filtroStanza || r.stanza === filtroStanza) &&
          (!filtroLavoro || r.lavoroId === filtroLavoro)
      ),
    [rilievi, filtroStanza, filtroLavoro]
  );

  const totaleFoto = useMemo(
    () => rilievi.reduce((s, r) => s + (r.foto?.length ?? 0), 0),
    [rilievi]
  );

  async function salvaNuovo() {
    if (!projectId || !nuovo?.stanza.trim()) return;
    setErrore(null);
    try {
      await creaRilievo.mutateAsync({
        projectId,
        stanza: nuovo.stanza.trim(),
        lavoroId: nuovo.lavoroId || null,
        titolo: nuovo.titolo.trim() || null,
        testo: nuovo.testo.trim() || null,
        data: oggi(),
      });
      setNuovo(null);
    } catch {
      setErrore('Non sono riuscito a salvare il rilievo. Riprova.');
    }
  }

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-gray-900">Nessun progetto selezionato</h2>
        <p className="mt-2 text-gray-600">Seleziona un progetto per vedere i rilievi.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rilievi</h1>
          <p className="mt-1 text-sm text-gray-600">
            Quello che vedi sul posto: foto e note, per stanza e per lavorazione.
            {rilievi.length > 0 && (
              <span className="text-gray-500">
                {' '}
                · {rilievi.length} rilievi, {totaleFoto} foto
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => setNuovo({ stanza: filtroStanza, lavoroId: filtroLavoro, titolo: '', testo: '' })}
          className="btn-primary"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuovo rilievo
        </button>
      </div>

      {errore && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 rounded-lg p-3 text-sm">
          {errore}
        </div>
      )}

      {/* Filtri */}
      {rilievi.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-lg p-3">
          <select value={filtroStanza} onChange={(e) => setFiltroStanza(e.target.value)} className="text-sm">
            <option value="">Tutte le stanze</option>
            {stanzeUsate.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select value={filtroLavoro} onChange={(e) => setFiltroLavoro(e.target.value)} className="text-sm">
            <option value="">Tutte le lavorazioni</option>
            {lavori.map((l) => (
              <option key={l.id} value={l.id}>{l.nome}</option>
            ))}
          </select>
          {(filtroStanza || filtroLavoro) && (
            <button
              onClick={() => { setFiltroStanza(''); setFiltroLavoro(''); }}
              className="text-sm text-primary-600 hover:underline"
            >
              azzera filtri
            </button>
          )}
          <span className="text-xs text-gray-500 ml-auto">
            {visibili.length} di {rilievi.length}
          </span>
        </div>
      )}

      {/* Nuovo rilievo */}
      {nuovo && (
        <div className="bg-white border border-primary-200 rounded-xl p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="input-label" htmlFor="ril-stanza">Stanza o ambito</label>
              <input
                id="ril-stanza"
                list="stanze-suggerite"
                value={nuovo.stanza}
                onChange={(e) => setNuovo({ ...nuovo, stanza: e.target.value })}
                placeholder="Cucina, Sottotetto, Copertura…"
                className="w-full"
                maxLength={100}
                autoFocus
              />
              <datalist id="stanze-suggerite">
                {stanzeSuggerite.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="input-label" htmlFor="ril-lavoro">Lavorazione (facoltativa)</label>
              <select
                id="ril-lavoro"
                value={nuovo.lavoroId}
                onChange={(e) => setNuovo({ ...nuovo, lavoroId: e.target.value })}
                className="w-full"
              >
                <option value="">— nessuna —</option>
                {lavori.map((l) => (
                  <option key={l.id} value={l.id}>{l.nome}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="input-label" htmlFor="ril-titolo">Titolo (facoltativo)</label>
            <input
              id="ril-titolo"
              value={nuovo.titolo}
              onChange={(e) => setNuovo({ ...nuovo, titolo: e.target.value })}
              placeholder="Traccia dietro il frigo"
              className="w-full"
              maxLength={200}
            />
          </div>
          <div>
            <label className="input-label" htmlFor="ril-testo">Nota</label>
            <textarea
              id="ril-testo"
              value={nuovo.testo}
              onChange={(e) => setNuovo({ ...nuovo, testo: e.target.value })}
              placeholder="Misure rilevate, cosa hai trovato, a cosa stare attenti…"
              className="w-full"
              rows={3}
              maxLength={5000}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={salvaNuovo}
              disabled={!nuovo.stanza.trim() || creaRilievo.isPending}
              className="btn-primary"
            >
              {creaRilievo.isPending ? 'Salvo…' : 'Crea, poi aggiungo le foto'}
            </button>
            <button onClick={() => setNuovo(null)} className="btn-secondary">Annulla</button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-500">Carico…</p>}

      {!isLoading && rilievi.length === 0 && !nuovo && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <h3 className="text-lg font-medium text-gray-900">Ancora nessun rilievo</h3>
          <p className="mt-2 text-sm text-gray-500 max-w-lg mx-auto">
            Le foto più preziose sono quelle delle tracce prima di chiuderle: fra sei mesi, per sapere
            dove passa un tubo, l&apos;unica alternativa è il martello.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {visibili.map((r) => (
          <CardRilievo
            key={r.id}
            rilievo={r}
            projectId={currentProject.id}
            lavori={lavori}
            onCaricaFoto={caricaFoto.mutateAsync}
            onAggiorna={(data) => aggiornaRilievo.mutate({ id: r.id, projectId: currentProject.id, data })}
            onElimina={() => {
              const n = r.foto?.length ?? 0;
              const msg = n > 0
                ? `Eliminare il rilievo "${r.titolo || r.stanza}" e le sue ${n} foto?`
                : `Eliminare il rilievo "${r.titolo || r.stanza}"?`;
              if (window.confirm(msg)) {
                eliminaRilievo.mutate({ id: r.id, projectId: currentProject.id });
              }
            }}
            onIngrandisci={setIngrandita}
            onErrore={setErrore}
          />
        ))}
      </div>

      {/* Foto a schermo intero */}
      {ingrandita && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setIngrandita(null)}
        >
          <FotoProtetta
            documentoId={ingrandita.id}
            alt={ingrandita.nome}
            className="max-h-full max-w-full object-contain rounded"
          />
          <button
            onClick={() => setIngrandita(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl leading-none"
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function CardRilievo({
  rilievo,
  projectId,
  lavori,
  onCaricaFoto,
  onAggiorna,
  onElimina,
  onIngrandisci,
  onErrore,
}: {
  rilievo: RilievoType;
  projectId: string;
  lavori: Lavoro[];
  onCaricaFoto: (v: { projectId: string; rilievoId: string; nome: string; file: FilePronto }) => Promise<unknown>;
  onAggiorna: (data: { testo?: string | null; lavoroId?: string | null }) => void;
  onElimina: () => void;
  onIngrandisci: (f: { id: string; nome: string }) => void;
  onErrore: (m: string | null) => void;
}) {
  const inputFile = useRef<HTMLInputElement>(null);
  const [caricamento, setCaricamento] = useState<string | null>(null);
  const [modificaNota, setModificaNota] = useState(false);
  const [testo, setTesto] = useState(rilievo.testo ?? '');

  const foto = rilievo.foto ?? [];

  async function aggiungiFoto(files: FileList | null) {
    if (!files?.length) return;
    onErrore(null);

    for (const [i, file] of Array.from(files).entries()) {
      setCaricamento(`${i + 1} di ${files.length}`);
      try {
        const pronto = await preparaFoto(file);
        await onCaricaFoto({ projectId, rilievoId: rilievo.id, nome: file.name, file: pronto });
      } catch {
        onErrore(`Non sono riuscito a caricare "${file.name}". Le altre foto sono state salvate.`);
      }
    }

    setCaricamento(null);
    if (inputFile.current) inputFile.current.value = '';
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-start justify-between gap-3 p-4 pb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">
              {rilievo.stanza}
            </span>
            {rilievo.lavoro && (
              <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                {rilievo.lavoro.nome}
              </span>
            )}
            <span className="text-xs text-gray-400">
              {new Date(rilievo.data).toLocaleDateString('it-IT')}
            </span>
          </div>
          {rilievo.titolo && (
            <h3 className="mt-1.5 text-base font-semibold text-gray-900">{rilievo.titolo}</h3>
          )}
        </div>
        <button
          onClick={onElimina}
          className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg shrink-0"
          title="Elimina rilievo"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Nota */}
      <div className="px-4 pb-3">
        {modificaNota ? (
          <div className="space-y-2">
            <textarea
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
              className="w-full text-sm"
              rows={3}
              maxLength={5000}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => { onAggiorna({ testo: testo.trim() || null }); setModificaNota(false); }}
                className="btn-primary text-xs py-1.5 px-3"
              >
                Salva
              </button>
              <button
                onClick={() => { setTesto(rilievo.testo ?? ''); setModificaNota(false); }}
                className="btn-secondary text-xs py-1.5 px-3"
              >
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setModificaNota(true)}
            className="text-left w-full text-sm text-gray-700 hover:bg-gray-50 rounded p-2 -m-2 whitespace-pre-wrap"
          >
            {rilievo.testo || <span className="text-gray-400 italic">Aggiungi una nota…</span>}
          </button>
        )}
      </div>

      {/* Foto */}
      {foto.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-1 px-4 pb-3">
          {foto.map((f) => (
            <button
              key={f.id}
              onClick={() => onIngrandisci({ id: f.id, nome: f.nome })}
              className="aspect-square overflow-hidden rounded-lg bg-gray-100 hover:opacity-90 transition-opacity"
              title={`${f.nome} · ${formatoPeso(f.size)}`}
            >
              <FotoProtetta documentoId={f.id} alt={f.nome} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-t border-gray-100">
        <input
          ref={inputFile}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => aggiungiFoto(e.target.files)}
        />
        <button
          onClick={() => inputFile.current?.click()}
          disabled={!!caricamento}
          className="btn-secondary text-xs py-1.5 px-3"
        >
          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {caricamento ? `Carico ${caricamento}…` : foto.length ? 'Aggiungi foto' : 'Aggiungi le foto'}
        </button>
        <select
          value={rilievo.lavoroId ?? ''}
          onChange={(e) => onAggiorna({ lavoroId: e.target.value || null })}
          className="text-xs py-1.5 ml-auto"
          title="Lavorazione collegata"
        >
          <option value="">— nessuna lavorazione —</option>
          {lavori.map((l) => (
            <option key={l.id} value={l.id}>{l.nome}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
